import {useCallback, useRef, useState, type ReactNode} from 'react';
import type {Area} from 'react-easy-crop';
import {safeCaptureException} from '../../utils/sentry';
import {useTranslation} from 'react-i18next';
import {Camera, ImageIcon} from 'lucide-react';
import {Button, cn} from '@chekinapp/ui';
import {getCroppedImage} from '../../utils/getCroppedImage';
import {ImageCropper} from '../PhotoBox/components/ImageCropper';
import {CaptureDock} from './CaptureDock';

type PhotoUploadProps = {
  image: string;
  onConfirm: (image: string) => void;
  onChangePhoto: () => void;
  hints?: ReactNode;
  onUseCamera?: () => void;
  confirmLabel?: string;
  className?: string;
};

export function PhotoUpload({
  image,
  onConfirm,
  onChangePhoto,
  hints,
  onUseCamera,
  confirmLabel,
  className,
}: PhotoUploadProps) {
  const {t} = useTranslation('identity-verification');
  const [isCropping, setIsCropping] = useState(false);
  const croppedAreaPixelsRef = useRef<Area | null>(null);

  const handleConfirm = useCallback(async () => {
    if (!croppedAreaPixelsRef.current) return;

    setIsCropping(true);
    try {
      onConfirm(await getCroppedImage(image, croppedAreaPixelsRef.current));
    } catch (err) {
      safeCaptureException(err, 'Cropping the uploaded photo');
      setIsCropping(false);
    }
  }, [image, onConfirm]);

  return (
    <div
      className={cn('iv-photo-upload flex flex-col items-center gap-4 w-full', className)}
    >
      <ImageCropper
        image={image}
        onCropComplete={pixels => {
          croppedAreaPixelsRef.current = pixels;
        }}
      />

      {hints}

      <CaptureDock>
        <div className="iv-photo-upload__actions flex w-full flex-col items-center gap-2.5">
          <Button
            onClick={handleConfirm}
            disabled={isCropping}
            className="w-full max-w-[340px] lg:w-[280px]"
          >
            {confirmLabel ?? t('confirm_photo')}
          </Button>
          <Button
            variant="outline"
            onClick={onChangePhoto}
            disabled={isCropping}
            className="w-full max-w-[340px] lg:w-[280px]"
          >
            <ImageIcon />
            {t('choose_another_photo')}
          </Button>
          <div className="flex w-full flex-wrap items-center justify-center gap-x-[18px] gap-y-1">
            {onUseCamera && (
              <Button
                variant="link"
                onClick={onUseCamera}
                disabled={isCropping}
                className="py-2"
              >
                <Camera className="h-4 w-4" />
                {t('take_a_photo_instead')}
              </Button>
            )}
          </div>
        </div>
      </CaptureDock>
    </div>
  );
}
